import { useAuthentication } from '@infominds/react-native-license'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useAtom } from 'jotai/react'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import React, { useEffect, useState } from 'react'
import { Platform, StyleSheet } from 'react-native'
import Spinner from 'react-native-modal-spinner'

import api from '../apis/Api'
import BillsList from '../components/BillsList'
import SearchBar from '../components/SearchBar'
import { View } from '../components/Themed'
import Colors from '../constants/Colors'
import Layout from '../constants/Layout'
import global from '../global'
import useSettings from '../hooks/useSettings'
import { ArchiveScreenNavigationProp, Bill, HomeScreenNavigationProp } from '../types'
import CommentView from './CommentView'
import DetailView from './DetailView'
import MasterDetail from './MasterDetail'

export type GroupOptions = 'billDate' | 'supplierName' | 'firstDueDate'

const selectedGroupByStateAtom = atomWithStorage<number>(
  'selectedGroupBy',
  0,
  createJSONStorage<number>(() => AsyncStorage)
)
const groupByStateAtom = atomWithStorage<GroupOptions>(
  'groupBy',
  'billDate',
  createJSONStorage<GroupOptions>(() => AsyncStorage)
)

const groupByOptions: GroupOptions[] = ['billDate', 'supplierName', 'firstDueDate']

interface Props {
  isArchive: boolean
}

export default function BillListView({ isArchive }: Props) {
  const { historyDays } = useSettings()
  const { url, token, company } = useAuthentication()
  const homeStackNavigation = useNavigation<HomeScreenNavigationProp>()
  const archiveStackNavigation = useNavigation<ArchiveScreenNavigationProp>()

  const [data, setData] = useState<Bill[]>([])
  const [search, updateSearch] = useState('')
  const [loading, setLading] = useState(false)
  const [selectedBill, setSelectedBill] = useState<Bill | undefined>()

  const [groupBy, setGroupBy] = useAtom(groupByStateAtom)
  const [selectedGroupBy, setSelectedGroupBy] = useAtom(selectedGroupByStateAtom)

  function getData() {
    if (isArchive) {
      return api.getArchive(token, url, company, historyDays) as Promise<{ data: Bill[] }>
    }
    return api.getBills(token, url, company)
  }

  const isFocused = useIsFocused()

  useEffect(() => {
    if (!isFocused) {
      return
    }
    reload()
  }, [token, url, company, historyDays, isArchive, isFocused])

  useEffect(() => {
    setGroupBy(groupByOptions[selectedGroupBy]).catch(console.error)
  }, [selectedGroupBy])

  const nexGroupBy = () =>
    setSelectedGroupBy(async val => {
      let savedValue = await val
      return ++savedValue % groupByOptions.length
    })

  const reload = () => {
    setLading(true)

    getData()
      .then(async result => {
        const dowait = await AsyncStorage.getItem('wait').catch(console.error)

        if (!isArchive && !JSON.parse(String(dowait))) {
          // filter blacklisted bills out
          global.billBlacklist.forEach((blackbill: Bill) => {
            const idx = result.data.findIndex(dataBill => {
              return blackbill.billID === dataBill.billID && blackbill.supplierID === dataBill.supplierID && blackbill.billDate === dataBill.billDate
            })

            if (idx >= 0) {
              result.data.splice(idx, 1)
            }
          })
        }

        return setData(result.data)
      })
      .catch(console.error)
      .finally(() => {
        setLading(false)
      })
  }

  return (
    <View style={styles.container}>
      {Platform.OS !== 'web' && !Layout.isSmallDevice ? (
        <Spinner color={Colors.tint} size="large" backgroundColor="transparent" visible={loading} />
      ) : (
        <></>
      )}
      <MasterDetail
        master={
          // eslint-disable-next-line react-native/no-inline-styles
          <View style={{ height: '100%' }}>
            <SearchBar text={search} onChangeText={updateSearch} onGroupPress={() => nexGroupBy()} />
            <BillsList
              data={data}
              refreshing={loading && Layout.isSmallDevice}
              refresh={reload}
              selectedBill={selectedBill}
              onSelectedBillChanged={item => {
                if (!Layout.isSmallDevice) {
                  setSelectedBill(item)
                } else {
                  if (!isArchive) {
                    homeStackNavigation.navigate('DetailScreen', { bill: item })
                  } else {
                    archiveStackNavigation.navigate('HistoryScreen', { bill: item })
                  }
                }
              }}
              searchText={search}
              groupBy={groupBy}
              isOthers={true}
              isArchive={isArchive}
              hasRightSwipe={!isArchive}
            />
          </View>
        }
        detail={
          selectedBill ? (
            <>
              {!isArchive ? (
                <DetailView
                  bill={selectedBill}
                  onDone={() => {
                    reload()
                    setSelectedBill(undefined)
                  }}
                />
              ) : (
                <CommentView bill={selectedBill} />
              )}
            </>
          ) : (
            <View style={styles.detailPage} />
          )
        }
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 2,
    flexDirection: 'column',
  },
  detailPage: {
    margin: 10,
  },
})
